/**
 * MER-211: pure aggregation over dashboard_event rows (migration
 * 0040) for the /admin/usage viewer. Kept dependency-free from
 * Supabase/Chart.js so node:test can pin the "5 minutes of telemetry
 * still renders sensibly, no NaN/undefined" acceptance criterion
 * without a live round trip or a DOM.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

function toDayKey(occurredAt) {
  return typeof occurredAt === "string" ? occurredAt.slice(0, 10) : null;
}

/** count(*) where event_type='login' and occurred_at within the last `days`. */
export function sessionsInWindow(events, days, now = Date.now()) {
  const cutoff = now - days * DAY_MS;
  let count = 0;
  for (const e of events ?? []) {
    if (e.event_type !== "login") continue;
    const t = Date.parse(e.occurred_at);
    if (!Number.isNaN(t) && t >= cutoff) count += 1;
  }
  return count;
}

/** max(occurred_at) where event_type='login', or null if no logins. */
export function lastLoginAt(events) {
  let latest = null;
  for (const e of events ?? []) {
    if (e.event_type !== "login" || typeof e.occurred_at !== "string") continue;
    if (latest === null || e.occurred_at > latest) latest = e.occurred_at;
  }
  return latest;
}

/** count(distinct user_id) across ALL event types within the last `days`. */
export function activeUsersInWindow(events, days, now = Date.now()) {
  const cutoff = now - days * DAY_MS;
  const seen = new Set();
  for (const e of events ?? []) {
    const t = Date.parse(e.occurred_at);
    if (!Number.isNaN(t) && t >= cutoff && e.user_id) seen.add(e.user_id);
  }
  return seen.size;
}

/**
 * Daily login counts for the last `days` days, oldest first,
 * zero-filled so a gap renders as 0 rather than vanishing from the
 * sparkline. Keys are yyyy-mm-dd (UTC, matching the DB's ISO stamps).
 */
export function dailySessionCounts(events, days, now = Date.now()) {
  const buckets = new Map();
  for (let i = days - 1; i >= 0; i -= 1) {
    const key = toDayKey(new Date(now - i * DAY_MS).toISOString());
    buckets.set(key, 0);
  }
  for (const e of events ?? []) {
    if (e.event_type !== "login") continue;
    const key = toDayKey(e.occurred_at);
    if (key !== null && buckets.has(key)) buckets.set(key, buckets.get(key) + 1);
  }
  return Array.from(buckets.entries()).map(([date, count]) => ({ date, count }));
}

/**
 * Top N distinct string values of `payload[field]` across events of
 * `eventType`, most-frequent first. Non-string / empty payload values
 * are skipped rather than counted as "undefined".
 */
export function topByPayloadField(events, eventType, field, n = 10) {
  const counts = new Map();
  for (const e of events ?? []) {
    if (e.event_type !== eventType) continue;
    const payload = e.payload && typeof e.payload === "object" ? e.payload : {};
    const value = payload[field];
    if (typeof value !== "string" || value.length === 0) continue;
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    .slice(0, n);
}

/** One row per user_id with a login event, most-recent login first. */
export function lastLoginPerUser(events) {
  const map = new Map();
  for (const e of events ?? []) {
    if (e.event_type !== "login" || !e.user_id) continue;
    const prev = map.get(e.user_id);
    if (!prev || e.occurred_at > prev) map.set(e.user_id, e.occurred_at);
  }
  return Array.from(map.entries())
    .map(([userId, at]) => ({ userId, lastLoginAt: at }))
    .sort((a, b) => (a.lastLoginAt < b.lastLoginAt ? 1 : -1));
}

/**
 * "up" | "down" | "flat" vs the prior window. A >5% relative move
 * either direction counts as a trend; anything tighter (or 0 -> 0)
 * reads flat rather than a noisy arrow on single-digit counts.
 */
export function trendDirection(current, prior) {
  if (prior === 0) return current > 0 ? "up" : "flat";
  const delta = (current - prior) / prior;
  if (delta > 0.05) return "up";
  if (delta < -0.05) return "down";
  return "flat";
}
